import React, { useState, useEffect } from "react";
import { 
  Row, 
  Col, 
  Button, 
  Input, 
  Alert, 
  Spinner,
  CustomInput,
  Table,
  Card,
  CardBody,
  CardHeader,
  Badge,
  InputGroup,
  InputGroupAddon
} from "reactstrap";
import axios from "axios";
import { API_URL, getApiConfig } from "views/composant/Config/apiConfig.js";
import { useParams } from "react-router-dom";

// Styles personnalisés
const styles = {
  cardHeader: {
    background: 'linear-gradient(to right, #1e88e5, #0c6fc0)',
    color: 'white',
    borderRadius: '8px 8px 0 0',
    padding: '15px 20px',
  },
  cardBody: {
    padding: '20px',
    backgroundColor: '#f8fcff',
  },
  emailBadge: {
    background: '#9bc4de',
    color: 'white',
    borderRadius: '20px',
    padding: '8px 12px',
    margin: '0 8px 8px 0',
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '0.9rem',
  },
  removeIcon: {
    marginLeft: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  button: {
    background: '#0c6fc0',
    borderColor: '#0c6fc0',
    boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
  },
  secondaryButton: {
    background: '#9bc4de',
    borderColor: '#9bc4de',
    boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
  },
  activeButton: {
    background: '#2ca8ff',
    borderColor: '#2ca8ff',
    boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
  },
  saveButton: {
    background: '#2ca8ff',
    borderColor: '#2ca8ff',
    boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
    padding: '10px 20px',
    transition: 'all 0.2s ease',
  },
  table: {
    boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
    borderRadius: '8px',
    overflow: 'hidden',
    tableLayout: 'fixed',  // Forcer une mise en page fixe pour respecter les largeurs des colonnes
  },
  tableHeader: {
    background: 'linear-gradient(to right, #1e88e5, #0c6fc0)',
    color: 'white',
    textTransform: 'uppercase',
    fontWeight: '500',
    fontSize: '0.85rem',
    padding: '12px 15px',
  },
  // Styles spécifiques pour les colonnes du tableau
  tableCellCapteur: {
    width: '18%',  // Taille pour la colonne CAPTEUR
  },
  tableCellType: {
    width: '32%',  // Taille pour la colonne TYPE (augmentée)
  },
  tableCellMin: {
    width: '12%',  // Taille réduite pour la colonne MIN
  },
  tableCellMax: {
    width: '12%',  // Taille réduite pour la colonne MAX
  },
  tableCellAlerte: {
    width: '12%',  // Taille pour la colonne ALERTE
  },
  tableCellAction: {
    width: '14%',  // Taille pour la colonne ACTION
  },
  inputField: {
    borderRadius: '6px',
    border: '1px solid #e0e6ed',
    padding: '10px 15px',
    transition: 'border 0.2s ease',
  },
  switch: {
    '& .custom-control-input:checked ~ .custom-control-label::before': {
      backgroundColor: '#0c6fc0',
      borderColor: '#0c6fc0',
    }
  },
  typeBadge: {
    background: '#1e88e5',
    color: 'white',
    borderRadius: '15px',
    padding: '4px 10px',
    fontSize: '0.8rem',
    display: 'inline-flex',
    alignItems: 'center',
    fontWeight: 'bold'
  },
  // Style pour personnaliser l'apparence des options dans le dropdown
  dropdownOption: {
    fontSize: '0.75rem',
    whiteSpace: 'normal',
    wordWrap: 'break-word'
  }
};

export default function AlertMailSetup({ boitierId }) {
  // Utiliser soit l'ID passé en prop, soit l'ID de l'URL
  const { id: urlId } = useParams();
  const id = boitierId || urlId;
  
  // Types de capteurs disponibles
  const capteurTypes = [
    { id: "2", name: "Capteur 4 à 20 mA (Config 0 à 100°C)", label: "Température", icon: "fa-thermometer-half" }
    // { id: "3", name: "Capteur 0 à 20 mA (Config 0 à 100°C)", label: "Pression", icon: "fa-tachometer-alt" },
    // { id: "4", name: "Capteur 0 à 20 mA (Config 0 à 100°C)", label: "Humidité", icon: "fa-tint" },
    // { id: "5", name: "Capteur 0 à 20 mA (Config 0 à 100°C)", label: "Niveau", icon: "fa-water" },
    // { id: "6", name: "Capteur 0 à 20 mA (Config 0 à 100°C)", label: "Débit", icon: "fa-faucet" }
    // Ajouter d'autres types de capteurs ici au besoin
  ];
  
  // États pour la gestion des alertes configurables
  const [configAlerts, setConfigAlerts] = useState([]);
  const [capteurInfos, setCapteurInfos] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [modified, setModified] = useState({});
  
  // État pour suivre les modifications des noms de capteurs
  const [capteurLabelsModified, setCapteurLabelsModified] = useState({});
  
  // État pour les emails de contact (toujours sous forme de chaîne séparée par des virgules)
  const [contactEmails, setContactEmails] = useState("");
  const [emailModified, setEmailModified] = useState(false);

  // Chargement des alertes configurables à l'initialisation
  useEffect(() => {
    fetchConfigurableAlerts();
    // La récupération des emails se fait maintenant directement dans fetchConfigurableAlerts
  }, [id]);

  // Fonction pour enregistrer les emails de contact
  const saveContactEmails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Utilise la route configAlert existante et inclut le mail_alerte
      const payload = {
        alerts: configAlerts.map(alert => ({
          id: alert.id,
          valuemin: alert.valuemin,
          valuemax: alert.valuemax,
          active: alert.active,
          capteur_id: alert.capteur_id,
          interne: alert.interne,
          mail: alert.mail,
          sms: alert.sms,
          telephone: alert.telephone,
          config: alert.config
        })),
        capteurs: {},
        mail_alerte: contactEmails // Ajouter le mail_alerte au payload
      };
      
      const response = await axios.post(
        `${API_URL}configAlert/${id}`,
        payload,
        getApiConfig()
      );
      
      if (response.data.status === "success") {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        setEmailModified(false);
      } else {
        setError("Erreur lors de la mise à jour des emails de contact");
      }
    } catch (err) {
      console.error("Erreur lors de la mise à jour des emails de contact:", err);
      setError("Erreur lors de la mise à jour des emails: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour gérer la modification des emails
  const handleEmailChange = (e) => {
    setContactEmails(e.target.value);
    setEmailModified(true);
  };

  // Fonction pour supprimer un email
  const removeEmail = (emailToRemove) => {
    const emailList = contactEmails.split(",").map(e => e.trim());
    const updatedList = emailList.filter(email => email !== emailToRemove);
    setContactEmails(updatedList.join(", "));
    setEmailModified(true);
  };

  // Fonction pour récupérer les alertes configurables et les emails d'alerte
  const fetchConfigurableAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(
        `${API_URL}configAlert/${id}`,
        getApiConfig()
      );
      
if (response.data.status === "success") {
  // Filtrer pour exclure le capteur interne
  const filteredAlerts = response.data.data.alerts
    .filter(alert => !alert.interne)
    .map(alert => ({
      ...alert,
      // Forcer mail à être égal à active dès le chargement
      mail: alert.active
    }));
  
  setConfigAlerts(filteredAlerts);
  setCapteurInfos(response.data.data.capteurs);
  
  // Récupérer l'adresse email d'alerte du boîtier
  if (response.data.data.mail_alerte) {
    setContactEmails(response.data.data.mail_alerte);
  }
} else {
        setError("Erreur lors de la récupération des alertes");
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des alertes:", err);
      setError("Erreur lors de la récupération des alertes: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour mettre à jour une valeur d'alerte
  const handleValueChange = (alertId, field, value) => {
    setConfigAlerts(prevAlerts => 
      prevAlerts.map(alert => 
        alert.id === alertId ? { ...alert, [field]: value } : alert
      )
    );
    
    setModified(prev => ({ ...prev, [alertId]: true }));
  };

  // Fonction pour activer/désactiver une alerte
const handleToggleActive = (alertId, currentValue) => {
  // Met à jour à la fois 'active' et 'mail' avec la même valeur
  const newValue = !currentValue;
  
  setConfigAlerts(prevAlerts => 
    prevAlerts.map(alert => 
      alert.id === alertId 
        ? { ...alert, active: newValue, mail: newValue } 
        : alert
    )
  );
  
  setModified(prev => ({ ...prev, [alertId]: true }));
};

  // Fonction pour gérer la modification du nom (label) d'un capteur
  const handleCapteurLabelChange = (capteurId, newLabel) => {
    // Limiter la longueur à 20 caractères
    newLabel = newLabel.substring(0, 20);
    
    // Mettre à jour l'état des informations des capteurs
    setCapteurInfos(prev => ({
      ...prev,
      [capteurId]: {
        ...prev[capteurId],
        name: newLabel
      }
    }));
    
    // Marquer ce capteur comme modifié
    setCapteurLabelsModified(prev => ({
      ...prev,
      [capteurId]: true
    }));
  };
  
  // Fonction pour gérer la modification du type de capteur
  const handleCapteurTypeChange = (capteurId, newType) => {
    // Mettre à jour l'état des informations des capteurs
    setCapteurInfos(prev => ({
      ...prev,
      [capteurId]: {
        ...prev[capteurId],
        type: newType
      }
    }));
    
    // Marquer ce capteur comme modifié
    setCapteurLabelsModified(prev => ({
      ...prev,
      [capteurId]: true
    }));
  };

  // Fonction pour enregistrer les modifications d'une alerte
  const saveAlert = async (alertId) => {
    try {
      setLoading(true);
      setError(null);
      
      const alertToUpdate = configAlerts.find(a => a.id === alertId);
      if (!alertToUpdate) return;
      
      // Préparer les données à envoyer à l'API de la même manière que saveAllChanges
      const payload = {
        alerts: configAlerts.map(alert => {
          // Pour l'alerte spécifique, utiliser les valeurs modifiées
          if (alert.id === alertId) {
            return {
              id: alert.id,
              valuemin: alert.valuemin,
              valuemax: alert.valuemax,
              active: alert.active,
              capteur_id: alert.capteur_id,
              // Inclure les autres propriétés nécessaires
              interne: alert.interne,
              mail: alert.mail,
              sms: alert.sms,
              telephone: alert.telephone,
              config: alert.config
            };
          }
          // Pour les autres alertes, renvoyer tel quel
          return alert;
        }),
        capteurs: {}, // Pas de modification de capteurs ici
        mail_alerte: contactEmails // Inclure le mail_alerte
      };
      
      // Si le capteur associé à cette alerte a été modifié, l'inclure également
      const capteurId = alertToUpdate.capteur_id.toString();
      if (capteurLabelsModified[capteurId]) {
        payload.capteurs[capteurId] = {
          ...capteurInfos[capteurId],
          id: parseInt(capteurId),
          // Inclure explicitement le type pour s'assurer qu'il est envoyé à l'API
          type: capteurInfos[capteurId].type || "2"
        };
      }
      
      // Appel API pour mettre à jour l'alerte
      const response = await axios.post(
        `${API_URL}configAlert/${id}`,
        payload,
        getApiConfig()
      );
      
      if (response.data.status === "success") {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        
        // Réinitialiser l'état de modification
        setModified(prev => {
          const newState = { ...prev };
          delete newState[alertId];
          return newState;
        });
        
        // Réinitialiser l'état des capteurs modifiés pour celui associé à cette alerte
        const capteurId = alertToUpdate.capteur_id.toString();
        if (capteurLabelsModified[capteurId]) {
          setCapteurLabelsModified(prev => {
            const newState = { ...prev };
            delete newState[capteurId];
            return newState;
          });
        }
        
        // Réinitialiser l'état de modification des emails si nécessaire
        if (emailModified) {
          setEmailModified(false);
        }
      } else {
        setError("Erreur lors de la mise à jour: " + (response.data.message || "Erreur inconnue"));
      }
    } catch (err) {
      console.error("Erreur lors de la mise à jour de l'alerte:", err);
      setError("Erreur lors de la mise à jour: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour enregistrer toutes les modifications
  const saveAllChanges = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Vérifier s'il y a des modifications à enregistrer
      const hasAlertModifications = Object.keys(modified).length > 0;
      const hasCapteurLabelModifications = Object.keys(capteurLabelsModified).length > 0;
      const hasEmailModifications = emailModified;
      
      if (!hasAlertModifications && !hasCapteurLabelModifications && !hasEmailModifications) {
        setError("Aucune modification à enregistrer");
        setLoading(false);
        return;
      }

      // Préparer les données à envoyer à l'API
      const payload = {
        alerts: configAlerts.map(alert => ({
          id: alert.id,
          valuemin: alert.valuemin,
          valuemax: alert.valuemax,
          active: alert.active,
          capteur_id: alert.capteur_id,
          // Forcer mail à être égal à active
          mail: alert.active, 
          interne: alert.interne,
          sms: alert.sms,
          telephone: alert.telephone,
          config: alert.config
        })),
        capteurs: {},
        mail_alerte: contactEmails // Inclure le mail_alerte dans le payload
      };
      
      // Ajouter les informations des capteurs modifiés (nom et type)
      Object.keys(capteurLabelsModified).forEach(capteurId => {
        payload.capteurs[capteurId] = {
          ...capteurInfos[capteurId],
          // S'assurer que l'ID est inclus
          id: parseInt(capteurId),
          // Inclure explicitement le type pour s'assurer qu'il est envoyé à l'API
          type: capteurInfos[capteurId].type || "2"
        };
      });
      
      // Appel API pour mettre à jour les alertes et les noms de capteurs
      const response = await axios.post(
        `${API_URL}configAlert/${id}`,
        payload,
        getApiConfig()
      );
      
      if (response.data.status === "success") {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        
        // Réinitialiser les états de modification
        setModified({});
        setCapteurLabelsModified({});
        setEmailModified(false);
        
        // Rafraîchir les données
        fetchConfigurableAlerts();
      } else {
        setError("Erreur lors de la mise à jour: " + (response.data.message || "Erreur inconnue"));
      }
    } catch (err) {
      console.error("Erreur lors de la mise à jour:", err);
      setError("Erreur lors de la mise à jour: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading && configAlerts.length === 0) {
    return (
      <div className="text-center p-5">
        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-3 text-primary font-weight-bold">Chargement des configurations d'alerte...</p>
      </div>
    );
  }

  // Grouper les alertes par capteur_id (uniquement externes)
  const alertsByCapteur = {};
  configAlerts.forEach(alert => {
    if (!alert.interne && !alertsByCapteur[alert.capteur_id]) {
      alertsByCapteur[alert.capteur_id] = [];
    }
    if (!alert.interne) {
      alertsByCapteur[alert.capteur_id].push(alert);
    }
  });

  // Conversion de la chaîne d'emails en tableau pour l'affichage
  const emailArray = contactEmails ? contactEmails.split(",").map(e => e.trim()).filter(e => e) : [];

  // Style CSS global pour améliorer l'affichage des dropdowns
  const dropdownCss = `
    .type-dropdown option {
      font-size: 0.75rem;
      white-space: normal;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .type-dropdown {
      text-overflow: ellipsis;
      overflow: hidden;
    }
  `;

  // Maintenant, on retourne uniquement le contenu, pas la modale complète
  return (
    <div className="edition-alerte-container">
      <style>{dropdownCss}</style>
      {success && (
        <Alert color="success" className="shadow-sm" style={{ borderRadius: '8px' }}>
          <i className="fas fa-check-circle mr-2"></i> Les modifications ont été enregistrées avec succès!
        </Alert>
      )}
      
      {error && (
        <Alert color="danger" className="shadow-sm" style={{ borderRadius: '8px' }}>
          <i className="fas fa-exclamation-circle mr-2"></i> {error}
        </Alert>
      )}
      
      {/* Section emails de contact */}
      <Card className="mb-4 shadow-sm" style={{ borderRadius: '8px', border: 'none' }}>
        <CardHeader style={styles.cardHeader}>
          <h5 className="mb-0 font-weight-bold">
            <i className="fas fa-envelope mr-2"></i> Configuration des alertes
          </h5>
        </CardHeader>
        <CardBody style={styles.cardBody}>
          <Row>
            <Col md={12} className="mb-4">
              <label className="text-muted font-weight-bold">Emails actuels :</label>
              <div className="email-tags mt-2">
                {emailArray.length > 0 ? (
                  emailArray.map((email, index) => (
                    <span 
                      key={index}
                      style={styles.emailBadge}
                    >
                      <i className="fas fa-envelope-open mr-2"></i> {email}
                      <span 
                        style={styles.removeIcon}
                        onClick={() => removeEmail(email)}
                      >
                        &times;
                      </span>
                    </span>
                  ))
                ) : (
                  <p className="text-muted">Aucun email configuré</p>
                )}
              </div>
            </Col>
            <Col md={12} className="mb-3">
              <label className="text-muted font-weight-bold">Modifier les emails :</label>
              <Input
                type="textarea"
                className="mt-2 shadow-sm"
                rows="3"
                placeholder="Emails séparés par des virgules"
                value={contactEmails}
                onChange={handleEmailChange}
                style={styles.inputField}
              />
              <div className="mt-2">
                <small className="text-muted">
                  <i className="fas fa-info-circle mr-1"></i> Note: Saisissez les adresses email séparées par des virgules. Les modifications seront appliquées après avoir cliqué sur "Enregistrer".
                </small>
              </div>
            </Col>
            <Col md={12} className="mt-3 text-right">
              <Button
                color="primary"
                onClick={saveContactEmails}
                disabled={loading || !emailModified}
                style={styles.button}
                className="px-4 py-2"
              >
                {loading ? <Spinner size="sm" /> : <><i className="fas fa-save mr-2"></i> Enregistrer les emails de contact</>}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>
      
      {/* Table des alertes */}
      <div className="mb-4">
        <h5 className="mb-3 font-weight-bold text-primary">
          <i className="fas fa-bell mr-2"></i> Configuration des capteurs et des seuils d'alerte par capteurs
        </h5>
        <Table responsive bordered hover className="mb-3" style={styles.table}>
          <thead>
            <tr>
              <th style={{...styles.tableHeader, ...styles.tableCellCapteur}}>CAPTEUR</th>
              <th style={{...styles.tableHeader, ...styles.tableCellType}}>TYPE</th>
              <th style={{...styles.tableHeader, ...styles.tableCellMin}}>MIN (°C)</th>
              <th style={{...styles.tableHeader, ...styles.tableCellMax}}>MAX (°C)</th>
              <th style={{...styles.tableHeader, ...styles.tableCellAlerte}}>ALERTE ?</th>
              <th style={{...styles.tableHeader, ...styles.tableCellAction}}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(alertsByCapteur).map(capteurId => {
              const capteur = capteurInfos[capteurId];
              const alertsForCapteur = alertsByCapteur[capteurId][0];
              
              if (!alertsForCapteur) return null;
              
              return (
                <tr key={capteurId} className={(modified[alertsForCapteur.id] || capteurLabelsModified[capteurId]) ? "bg-light" : ""}>
                  <td className="font-weight-bold" style={styles.tableCellCapteur}>
                    <div className="d-flex align-items-center flex-column">
                      <Input 
                        type="text"
                        className="form-control-sm"
                        value={capteur?.name || `Capteur ${capteurId}`}
                        onChange={(e) => handleCapteurLabelChange(capteurId, e.target.value)}
                        maxLength={20}
                        style={{
                          ...styles.inputField,
                          width: '100%',
                          maxWidth: '150px',
                          marginBottom: '5px'
                        }}
                      />
                      {capteurLabelsModified[capteurId] && (
                        <small className="text-info">
                          <i className="fas fa-info-circle mr-1"></i>
                        </small>
                      )}
                    </div>
                  </td>
                  <td style={styles.tableCellType}>
                    <div className="d-flex flex-column align-items-center">
                      <div style={styles.typeBadge} className="mb-2">
                        <i className={`fas ${capteurTypes.find(t => t.id === (capteur?.type || "2"))?.icon || "fa-thermometer-half"} mr-1`} style={{fontSize: '0.8rem'}}></i>
                        {capteurTypes.find(t => t.id === (capteur?.type || "2"))?.label || "Température"}
                      </div>
                      <Input 
                        type="select" 
                        className="form-control-sm type-dropdown"
                        value={capteur?.type || "2"}
                        onChange={(e) => handleCapteurTypeChange(capteurId, e.target.value)}
                        style={{
                          ...styles.inputField,
                          width: '100%',
                          maxWidth: '280px',
                          fontSize: '0.75rem', // Texte encore plus petit
                          textOverflow: 'ellipsis', // Ajouter des points de suspension si le texte est trop long
                          whiteSpace: 'normal', // Permettre le retour à la ligne
                          overflow: 'hidden', // Cacher le texte qui déborde
                          paddingRight: '20px', // Espace pour la flèche du dropdown
                          paddingLeft: '5px', // Réduire le padding gauche
                          paddingTop: '5px', // Réduire le padding haut
                          paddingBottom: '5px' // Réduire le padding bas
                        }}
                      >
                        {capteurTypes.map(type => (
                          <option 
                            key={type.id} 
                            value={type.id} 
                            style={styles.dropdownOption}
                            title={type.name} // Ajouter un tooltip pour afficher le texte complet au survol
                          >
                            {type.name}
                          </option>
                        ))}
                      </Input>
                      {capteurLabelsModified[capteurId] && (
                        <small className="text-info mt-1">
                          <i className="fas fa-info-circle mr-1"></i>
                        </small>
                      )}
                    </div>
                  </td>
                  <td className="p-2" style={styles.tableCellMin}>
                    <InputGroup className="shadow-sm">
                      <Input 
                        type="number" 
                        value={alertsForCapteur.valuemin}
                        onChange={(e) => handleValueChange(alertsForCapteur.id, 'valuemin', parseFloat(e.target.value))}
                        min={0}
                        style={{...styles.inputField, width: '100%'}}
                      />
                    </InputGroup>
                  </td>
                  <td className="p-2" style={styles.tableCellMax}>
                    <InputGroup className="shadow-sm">
                      <Input 
                        type="number" 
                        value={alertsForCapteur.valuemax}
                        onChange={(e) => handleValueChange(alertsForCapteur.id, 'valuemax', parseFloat(e.target.value))}
                        min={0}
                        style={{...styles.inputField, width: '100%'}}
                      />
                    </InputGroup>
                  </td>
                  <td className="text-center align-middle" style={styles.tableCellAlerte}>
                    <CustomInput
                      type="switch"
                      id={`switch-${alertsForCapteur.id}`}
                      checked={alertsForCapteur.active}
                      onChange={() => handleToggleActive(alertsForCapteur.id, alertsForCapteur.active)}
                      style={styles.switch}
                    />
                  </td>
                  <td className="text-center" style={styles.tableCellAction}>
                    <Button
                      color="info"
                      onClick={() => saveAlert(alertsForCapteur.id)}
                      disabled={loading}
                      style={modified[alertsForCapteur.id] || capteurLabelsModified[capteurId] ? styles.activeButton : styles.secondaryButton}
                      className="px-3 py-2"
                    >
                      {loading && (modified[alertsForCapteur.id] || capteurLabelsModified[capteurId]) ? 
                        <Spinner size="sm" /> : 
                        <><i className="fas fa-save mr-1"></i> Enregistrer</>
                      }
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        
        <div className="text-center mt-4">
          <Button 
            color="primary" 
            onClick={saveAllChanges}
            disabled={loading || (Object.keys(modified).length === 0 && Object.keys(capteurLabelsModified).length === 0)}
            size="lg"
            style={Object.keys(modified).length > 0 || Object.keys(capteurLabelsModified).length > 0 ? styles.saveButton : {...styles.saveButton, opacity: 0.7}}
            className="px-5"
          >
            {loading ? 
              <Spinner size="sm" /> : 
              <><i className="fas fa-save mr-2"></i> Enregistrer toutes les modifications</>
            }
          </Button>
        </div>
      </div>
    </div>
  );
}
